import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@xbto/design-system';
import { Button, Col, Hr, Icon, Row, TextBody, TextSup, TextTitle, } from '../../atoms';
import { useBreakpoints } from '../../hooks';
import { DATE_FORMATS } from 'common';
import { formatWithFallback } from '../../hooks/use-date-format';
import { CollapsibleText } from '../../molecules/collapsible-text';
export const FundFacts = ({ data, isMarketAssetPage, onPressSubscribe, onPressDownloadFactSheet, }) => {
    var _a, _b;
    const { breakpoints } = useBreakpoints(500);
    const overViewItems = useMemo(() => {
        var _a, _b;
        return !data
            ? []
            : [
                {
                    id: 'inceptionDate',
                    title: 'Inception date',
                    value: formatWithFallback(data.inceptionDate, DATE_FORMATS.DD_MMM_YYYY, false),
                },
                {
                    id: 'subscriptions',
                    title: 'Subscriptions',
                    value: data.subscriptions,
                },
                {
                    id: 'liquidity',
                    title: 'Liquidity',
                    value: data.liquidity,
                },
                {
                    id: 'managementFee',
                    title: 'Management fee',
                    value: data.managementFee
                        ? `${data.managementFee.mul(100)}%`
                        : '',
                },
                {
                    id: 'performanceFee',
                    title: 'Performance fee',
                    value: data.performanceFee
                        ? `${data.performanceFee.mul(100)}%`
                        : '',
                },
                {
                    id: 'investmentManager',
                    title: 'Investment manager',
                    value: data.investmentManager,
                },
                {
                    id: 'fundAdmin',
                    title: 'Fund administrator',
                    value: data.fundAdmin,
                },
                {
                    id: 'auditor',
                    title: 'Auditor',
                    value: data.auditor,
                },
                {
                    id: 'isin',
                    title: 'ISIN',
                    value: data.isin,
                },
                {
                    id: 'minimumInvestmentInKind',
                    title: 'Minimum investment',
                    value: data.minimumInvestmentInKind
                        ? (_b = (_a = data.minimumInvestmentInKind) === null || _a === void 0 ? void 0 : _a.match(/(\*{1,2}|[^*]+)/g)) === null || _b === void 0 ? void 0 : _b.map(part => (_jsx(Fragment, { children: part === '*' ? (_jsx(TextSup, { verticalAlign: "-0.4em", children: "*" })) : (part) }, part)))
                        : null,
                },
                {
                    id: 'eligibleInvestors',
                    title: 'Eligible investors',
                    value: data.eligibleInvestors,
                },
            ];
    }, [data]);
    const overviewReturns = !data
        ? []
        : [
            { title: 'Return since inception', value: data.returnSinceInception },
            { title: 'Vol. since inception', value: data.volumeSinceInception },
            { title: 'Ret/Vol', value: data.returnVolumeRatio },
        ];
    if (!data) {
        return null;
    }
    return (_jsxs(Col.L, { style: [$.container, isMarketAssetPage && $.containerMarket], children: [_jsxs(Col.L, { style: $.titleContainer, children: [_jsx(TextTitle, { small: true, children: "Overview" }), _jsx(TextBody, { style: $.overview, children: data.overview || '-' })] }), isMarketAssetPage && (_jsxs(Col.L, { style: $.ctasAndReturnsContainer, children: [_jsxs(Row.L, { style: $.ctasContainer, children: [_jsx(Button, { label: "Subscribe", size: "sm", onPress: onPressSubscribe }), _jsx(Button, { label: "Download factsheet", size: "sm", variant: "tertiary", onPress: onPressDownloadFactSheet })] }), _jsx(Row.LR, { style: $.itemsReturnsContainer, children: overviewReturns.map(item => (_jsxs(Col.L, { style: [
                                $.itemReturnsContainer,
                                breakpoints.XS && $.itemReturnsContainer_XS,
                            ], children: [_jsx(TextBody, { small: true, lhFull: true, children: item.title }), _jsx(TextTitle, { em: true, children: item.value || '-' })] }, item.title))) })] })), isMarketAssetPage ? (_jsx(Hr, {})) : (_jsxs(Col.L, { style: $.minimumContainer, children: [_jsx(TextBody, { small: true, lhFull: true, children: "Minimum investment" }), _jsxs(Row.L, { style: $.minimum, children: [_jsx(TextTitle, { em: true, large: true, children: ((_a = data.minimumInvestmentInKind) === null || _a === void 0 ? void 0 : _a.replace('*', '')) || '' }), !!data.minimumInvestmentUsd && (_jsx(TextBody, { color: "secondary", children: data.minimumInvestmentUsd }))] })] })), _jsxs(Col.L, { children: [_jsx(Row.L, { style: $.itemsContainer, children: overViewItems.map(item => (_jsxs(Col.L, { style: $.itemContainer, children: [_jsx(TextBody, { small: true, lhFull: true, children: item.title }), _jsx(TextBody, { em: true, children: item.value || '-' })] }, item.id))) }), _jsx(Col.L, { style: $.itemsNotesContainer, children: _jsx(TextBody, { small: true, children: data.footnote }) })] }), isMarketAssetPage && (_jsxs(_Fragment, { children: [((_b = data.benefits) === null || _b === void 0 ? void 0 : _b.length) ? (_jsxs(Col.L, { style: $.benefitsContainer, children: [_jsx(TextTitle, { small: true, children: "Benefits" }), _jsx(Col.L, { style: $.benefitsContent, children: data.benefits.map((benefit, index) => (_jsxs(Row.T, { style: {
                                        columnGap: theme.spacing['6'],
                                        maxWidth: '100%',
                                    }, children: [_jsx(View, { style: $.iconWrapper, children: _jsx(Icon, { name: benefit.icon, size: 16, color: "primary" }) }), _jsxs(Col.L, { style: { flex: 1 }, children: [_jsx(TextBody, { em: true, children: benefit.title }), _jsx(TextBody, { children: benefit.text })] })] }, index))) }), _jsx(TextBody, { small: true, children: data.benefitsFootnote })] })) : null, data.disclaimer && (_jsxs(Col.L, { style: $.disclaimerContainer, children: [_jsx(TextTitle, { small: true, children: "Disclaimer" }), _jsx(CollapsibleText, { message: data.disclaimer })] }))] }))] }));
};
const $ = StyleSheet.create({
    container: {
        rowGap: theme.spacing['12'],
    },
    containerMarket: {
        rowGap: theme.spacing['8'],
    },
    titleContainer: {
        rowGap: theme.spacing['6'],
    },
    overview: {
        maxWidth: 696,
    },
    ctasAndReturnsContainer: {
        width: '100%',
        rowGap: theme.spacing['8'],
    },
    ctasContainer: {
        columnGap: theme.spacing['3'],
    },
    minimumContainer: {
        columnGap: theme.spacing['2'],
        rowGap: theme.spacing['4'],
    },
    minimum: {
        columnGap: theme.spacing['2'],
        rowGap: theme.spacing['2'],
    },
    itemsContainer: {
        flexWrap: 'wrap',
        columnGap: theme.spacing['4'],
        rowGap: theme.spacing['8'],
    },
    itemContainer: {
        width: 218.67,
        rowGap: theme.spacing['2'],
    },
    itemsReturnsContainer: {
        width: '100%',
        flexWrap: 'wrap',
        columnGap: theme.spacing['4'],
        rowGap: theme.spacing['6'],
    },
    itemReturnsContainer: {
        width: 209,
        rowGap: theme.spacing['2'],
    },
    itemReturnsContainer_XS: {
        width: 172.5,
    },
    itemsNotesContainer: {
        marginTop: theme.spacing['6'],
    },
    benefitsContainer: {
        rowGap: theme.spacing['6'],
    },
    benefitsContent: {
        rowGap: theme.spacing['6'],
    },
    iconWrapper: {
        width: 32,
        height: 32,
        borderRadius: theme.radius.sm,
        backgroundColor: theme.color.background.tertiary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    disclaimerContainer: {
        rowGap: theme.spacing['4'],
    },
});
